(() => {
  const selector = '[data-aegis-configurator]';
  const preparations = new WeakMap();

  function setReactInput(input, value) {
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    if (!setter) return;
    setter.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function applyBuilderHandoff(root) {
    const params = new URLSearchParams(window.location.search);
    const dimensions = ['width', 'height', 'depth'].map(key => params.get(`case_${key}`));
    if (dimensions.every(value => value === null)) return;
    if (dimensions.some(value => value === null || !/^\d{1,4}(?:\.\d{1,4})?$/.test(value) || Number(value) <= 0)) return;

    const unit = params.get('case_unit');
    const tier = params.get('case_tier');
    if (!['in', 'mm'].includes(unit) || !['shield', 'aegis'].includes(tier)) return;

    const currentUnit = root.querySelector('.ac-units button[aria-pressed="true"]')?.textContent?.trim();
    if (currentUnit !== unit) {
      [...root.querySelectorAll('.ac-units button')].find(button => button.textContent?.trim() === unit)?.click();
    }

    const inputs = [...root.querySelectorAll('.ac-dimensions input')];
    if (inputs.length !== 3) return;
    inputs.forEach((input, index) => setReactInput(input, dimensions[index]));
    root.querySelector(`[role="radio"][value="${tier}"]`)?.click();
  }

  function text(root, query) {
    return root.querySelector(query)?.textContent?.replace(/\s+/g, ' ').trim() || '—';
  }

  function row(label, value) {
    const wrapper = document.createElement('div');
    const term = document.createElement('dt');
    const description = document.createElement('dd');
    term.textContent = label;
    description.textContent = value;
    wrapper.append(term, description);
    return wrapper;
  }

  function parseMoney(value) {
    const amount = Number(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function configuration(root) {
    const inputs = [...root.querySelectorAll('.ac-dimensions input')];
    const quantity = Number(root.querySelector('#ac-quantity')?.value || 1);
    const unit = text(root, '.ac-units button[aria-pressed="true"]');
    return {
      width: inputs[0]?.value || '',
      height: inputs[1]?.value || '',
      depth: inputs[2]?.value || '',
      unit,
      tier: root.querySelector('[role="radio"][aria-checked="true"]')?.getAttribute('value') || 'shield',
      quantity,
    };
  }

  function preparationDetails(root) {
    const config = configuration(root);
    const totalCents = parseMoney(text(root, '.ac-price strong'));
    const expectedUnitCents = Math.round(totalCents / config.quantity);
    return { config, expectedUnitCents, key: JSON.stringify({ config, expectedUnitCents }) };
  }

  function prepareCase(host, root) {
    const details = preparationDetails(root);
    const existing = preparations.get(host);
    if (existing?.key === details.key) return existing.promise;
    const requestId = crypto.randomUUID();
    const promise = fetch(`${host.dataset.proxyPath || '/apps/aegis-case'}/cart-item`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        configuration: details.config,
        expectedUnitCents: details.expectedUnitCents,
        confirmedFlatPack: true,
        requestId,
      }),
    }).then(async response => {
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to prepare this case for the cart.');
      return data;
    }).catch(reason => {
      if (preparations.get(host)?.promise === promise) preparations.delete(host);
      throw reason;
    });
    preparations.set(host, { key: details.key, promise });
    return promise;
  }

  async function addPreparedCaseToCart(data, button, error) {
    const addPayload = JSON.stringify({
      items: [{ id: Number(data.variantId), quantity: data.quantity, properties: data.properties }],
    });
    let added;
    let detail;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt > 0) await new Promise(resolve => window.setTimeout(resolve, attempt * 750));
      added = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: addPayload,
      });
      if (added.ok) break;
      detail = await added.json().catch(() => null);
      const temporarilyUnavailable = added.status === 422 && /sold out|not available/i.test(detail?.description || '');
      if (!temporarilyUnavailable) break;
    }
    if (!added?.ok) throw new Error(detail?.description || 'Shopify could not add this case to the cart.');
    error.hidden = true;
    button.textContent = 'Added — opening cart…';
    window.location.assign('/cart');
  }

  function openReview(host, root) {
    root.querySelector('.aegis-order-review')?.remove();
    const inputs = [...root.querySelectorAll('.ac-dimensions input')];
    const unit = text(root, '.ac-units button[aria-pressed="true"]');
    const itemMeasurements = `${inputs.map(input => input.value).join(' × ')} ${unit}`;
    const dialog = document.createElement('dialog');
    dialog.className = 'aegis-order-review';
    dialog.setAttribute('aria-labelledby', 'aegis-order-review-title');

    const card = document.createElement('div');
    card.className = 'aegis-order-review__card';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'aegis-order-review__close';
    close.setAttribute('aria-label', 'Close order review');
    close.textContent = '×';
    const eyebrow = document.createElement('p');
    eyebrow.className = 'aegis-order-review__eyebrow';
    eyebrow.textContent = 'ORDER REVIEW';
    const title = document.createElement('h2');
    title.id = 'aegis-order-review-title';
    title.textContent = 'Review your custom case';
    const intro = document.createElement('p');
    intro.className = 'aegis-order-review__intro';
    intro.textContent = 'Confirm everything below before adding this custom case to your cart.';
    const details = document.createElement('dl');
    details.className = 'aegis-order-review__details';
    details.append(
      row('Item measurements', itemMeasurements),
      row('Finished inside space', text(root, '.ac-final-size strong')),
      row('Acrylic', text(root, '.ac-visual-heading > strong')),
      row('Quantity', root.querySelector('#ac-quantity')?.value || '1'),
      row('Delivery', 'Ships flat · assembly required')
    );
    const total = document.createElement('div');
    total.className = 'aegis-order-review__total';
    const totalLabel = document.createElement('span');
    totalLabel.textContent = 'Total';
    const totalValue = document.createElement('strong');
    totalValue.textContent = text(root, '.ac-price strong');
    total.append(totalLabel, totalValue);
    const shipping = document.createElement('p');
    shipping.className = 'aegis-order-review__shipping';
    shipping.textContent = 'Shipping and applicable tax are calculated at checkout.';
    const preparing = document.createElement('p');
    preparing.className = 'aegis-order-review__preparing';
    preparing.setAttribute('role', 'status');
    preparing.textContent = 'Preparing your custom case in the background…';
    const error = document.createElement('p');
    error.className = 'aegis-order-review__error';
    error.setAttribute('role', 'status');
    error.hidden = true;
    const actions = document.createElement('div');
    actions.className = 'aegis-order-review__actions';
    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'aegis-order-review__edit';
    edit.textContent = 'Edit case';
    const checkout = document.createElement('button');
    checkout.type = 'button';
    checkout.className = 'aegis-order-review__checkout';
    checkout.disabled = true;
    checkout.textContent = 'Preparing your case…';
    actions.append(edit, checkout);
    card.append(close, eyebrow, title, intro, details, total, shipping, preparing, error, actions);
    dialog.append(card);
    root.append(dialog);

    const dismiss = () => dialog.close();
    close.addEventListener('click', dismiss);
    edit.addEventListener('click', dismiss);
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dismiss();
    });
    dialog.addEventListener('close', () => dialog.remove(), { once: true });
    let preparedData = null;
    const startPreparation = () => {
      preparedData = null;
      checkout.disabled = true;
      checkout.textContent = 'Preparing your case…';
      preparing.hidden = false;
      preparing.textContent = 'Preparing your custom case in the background…';
      error.hidden = true;
      prepareCase(host, root).then(data => {
        if (!dialog.isConnected) return;
        preparedData = data;
        preparing.textContent = 'Your case is ready to add.';
        checkout.disabled = false;
        checkout.textContent = 'Add case to cart →';
      }).catch(reason => {
        if (!dialog.isConnected) return;
        error.textContent = reason instanceof Error ? reason.message : 'Unable to add this case to the cart.';
        error.hidden = false;
        preparing.hidden = true;
        checkout.disabled = false;
        checkout.textContent = 'Try preparing again →';
      });
    };
    checkout.addEventListener('click', async () => {
      if (!preparedData) {
        startPreparation();
        return;
      }
      checkout.disabled = true;
      checkout.textContent = 'Adding to cart…';
      error.hidden = true;
      try {
        await addPreparedCaseToCart(preparedData, checkout, error);
      } catch (reason) {
        error.textContent = reason instanceof Error ? reason.message : 'Unable to add this case to the cart.';
        error.hidden = false;
        checkout.disabled = false;
        checkout.textContent = 'Add case to cart →';
      }
    });
    dialog.showModal();
    startPreparation();
  }

  function patch(host) {
    const root = host.shadowRoot;
    if (!root || root.querySelector('.ac-review')) return false;
    const button = root.querySelector('.ac-buy');
    if (!button) return false;
    if (button.dataset.orderReviewPatched === 'true') return true;
    applyBuilderHandoff(root);
    if (!root.querySelector('style[data-aegis-order-review]')) {
      const style = document.createElement('style');
      style.dataset.aegisOrderReview = 'true';
      style.textContent = reviewCss;
      root.append(style);
    }
    button.dataset.orderReviewPatched = 'true';
    const label = [...button.childNodes].find(node => node.nodeType === Node.TEXT_NODE);
    if (label?.textContent?.includes('Buy this custom case')) label.textContent = 'Review your case';
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      openReview(host, root);
    }, true);
    const note = root.querySelector('.ac-checkout-note');
    const noteText = note && [...note.childNodes].find(node => node.nodeType === Node.TEXT_NODE);
    if (noteText) noteText.textContent = 'Review your case, add it to the cart, and keep shopping if you like. ';
    return true;
  }

  function mount(host) {
    if (patch(host)) return;
    const timer = window.setInterval(() => {
      if (patch(host)) window.clearInterval(timer);
    }, 100);
    window.setTimeout(() => window.clearInterval(timer), 10000);
  }

  function mountAll() {
    document.querySelectorAll(selector).forEach(mount);
  }

  const reviewCss = `.aegis-order-review{width:min(560px,calc(100vw - 28px));max-width:none;margin:auto;padding:0;border:0;border-radius:14px;background:transparent;color:#1d333a;box-shadow:0 24px 80px #071a24a6;font:16px/1.5 Arial,Helvetica,sans-serif}.aegis-order-review::backdrop{background:#071a24b8;backdrop-filter:blur(3px)}.aegis-order-review *{box-sizing:border-box}.aegis-order-review__card{position:relative;padding:32px;background:#fff;border-radius:14px}.aegis-order-review button{border:0;cursor:pointer;font:inherit}.aegis-order-review button:focus-visible{outline:2px solid #257950;outline-offset:3px}.aegis-order-review__close{position:absolute;right:15px;top:15px;display:grid;place-items:center;width:40px;height:40px;border-radius:50%;background:#eef3f2;color:#26433f;font-size:25px;line-height:1}.aegis-order-review__eyebrow{margin:0;color:#527368;font-size:12px;letter-spacing:1.4px;font-weight:600}.aegis-order-review h2{margin:8px 42px 8px 0;font-size:27px;line-height:1.2;color:#162d35}.aegis-order-review__intro{margin:0;color:#607780;font-size:14px;line-height:1.6}.aegis-order-review__details{margin:24px 0 18px;border-top:1px solid #dfe7e6}.aegis-order-review__details>div{display:flex;justify-content:space-between;gap:24px;padding:12px 0;border-bottom:1px solid #e5eceb}.aegis-order-review__details dt{font-size:13px;color:#6b8183}.aegis-order-review__details dd{margin:0;text-align:right;font-size:14px;font-weight:600;color:#29423f}.aegis-order-review__total{display:flex;justify-content:space-between;align-items:baseline;gap:20px;padding-top:4px}.aegis-order-review__total span{font-size:15px;font-weight:600}.aegis-order-review__total strong{font-size:30px;letter-spacing:-.7px;color:#153f2c}.aegis-order-review__shipping{margin:2px 0 0;text-align:right;font-size:12px;color:#708488}.aegis-order-review__preparing{margin:12px 0 0;padding:9px 12px;border-radius:6px;background:#edf6f1;color:#286344;font-size:12px;text-align:center}.aegis-order-review__error{margin:14px 0 0;padding:11px 13px;border-radius:6px;background:#fff3e9;color:#934020;font-size:13px;line-height:1.55}.aegis-order-review__actions{display:grid;grid-template-columns:1fr 2fr;gap:10px;margin-top:16px}.aegis-order-review__actions button{min-height:50px;border-radius:6px;font-size:14px;font-weight:600}.aegis-order-review__edit{background:#edf2f1;color:#31504c}.aegis-order-review__checkout{background:#276c47;color:#fff}.aegis-order-review__checkout:disabled{opacity:.65}@media(max-width:680px){.aegis-order-review__card{padding:26px 20px 20px}.aegis-order-review h2{font-size:23px}.aegis-order-review__details>div{gap:14px}.aegis-order-review__actions{grid-template-columns:1fr}.aegis-order-review__edit{order:2}.aegis-order-review__checkout{order:1}.aegis-order-review__total strong{font-size:27px}}`;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  else mountAll();
  document.addEventListener('shopify:section:load', mountAll);
})();
